/* eslint-disable prettier/prettier */
/* eslint-disable no-nested-ternary */
import type {
  DecoratingMethod,
  DesignPricingPayload,
  IAdditionalShippingPriceRes,
  IQuoteResponse,
  Location,
  QuoterPayload,
} from 'common/interfaces/quoter.interface';
import type { ICartItemDetail, ITimelineElement } from 'common/interfaces/cart.interface';
import { useState } from 'react';
import type {
  IDecorDetailInfo,
  IDesignGroupColorOption,
  IDesignGroupProductOption,
  IDesignLicensing,
  IDesignObj,
  IDesignProofItem,
  IPrintTypeInfo,
} from '../common/interfaces/design.interface';
import { currencyFormat } from './CommonUtils';
import { getAdditionalShippingType, SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY } from './ShippingUtils';
import { showToastrMessage } from './Toastr.Utils';
import type { IDesignItem, IDesignPricing } from '@/common/interfaces/design.interface';

import { FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD } from '@/constants/ShipTypes';
import type { GetQuoteMutationFunction } from '@/common/interfaces/order.interface';
import { LACE_DECORATING_METHOD_ID, LACE_MTO_COLOR } from '@/constants/DecorMethods';
import { V3_APP_BASE_URL } from '@/constants/Config';
import { designLicensePriorityToStatusMap } from '@/constants/DesignLicense';
import { getVariantSnapshotFromTransformedDesigns } from '@/utilities/DesignUtils';
import { mapDecorMethodInfoToQuoterDecoratingMethods } from '@/utilities/DecoratingMethodUtils';
import { getPromoProductQuoterFields } from '@/utilities/ApparelUtils';
import type { StoreCartMoqShippingLane } from '@/modules/Orders/CreateOrders/StoreCheckout/helpers/storeCheckoutMoqShipping.utils';

/** This checks if the print info string has print types other than screen printing */
const hasPrintTypeOtherThanScreenPrint = (printInfo: string) => {
  const locationStrings = printInfo.split(';');

  return locationStrings.some((locationStr) => {
    const [printingType] = locationStr.split(':').map(Number);
    return printingType !== 1;
  });
};

/** To get the locations info for designs created from DT if there are of screen printing print type
 * @param - printInfo - older format of print type info (eg: '1:4;5:3')
 */
const getDTLocations = (printInfo: string): Location[] => {
  if (!printInfo.trim()) {
    return [];
  }

  /** If any location has print type other than screen printing  */
  if (hasPrintTypeOtherThanScreenPrint(printInfo)) {
    return [];
  }

  const locationStrings = printInfo.split(';');

  return locationStrings.map((locationStr) => {
    const [printingType, numberOfColors] = locationStr.split(':').map(Number);
    return {
      printingType,
      printingVariant: 1,
      printingArea: 0,
      numberOfColors,
      isOversized: false,
    };
  });
};

/** Maps proof-details print type info to quoter location payloads. */
export const mapPrintTypeInfoToQuoterLocations = (
  printTypeInfo: IPrintTypeInfo[] | undefined
): Location[] =>
  printTypeInfo?.map((itm: IPrintTypeInfo) => {
    const location: Location = {
      printingType: itm.printType.id,
      printingVariant: itm?.variant?.id ?? 0,
      printingArea: itm?.estimatedSize?.id ?? 0,
      numberOfColors: itm.numberOfColors,
      isOversized: itm.oversized ?? false,
    };

    if (typeof itm.numberOfTags === 'number') {
      location.numberOfTags = itm.numberOfTags;
    }

    return location;
  }) ?? [];

/** This removes the '#' in the color code that we are adding while fetching the records in HQ */
const reverseHexCode = (hexCode: string) => {
  if (hexCode) {
    const isHashIncluded = hexCode.startsWith('#');

    if (isHashIncluded) {
      return hexCode.substring(1);
    }

    return hexCode;
  }

  return null;
};

/**
 * True when decorating methods include Lace with an MTO-only lace color.
 * Apparel color names are ignored — MTO is lace-decor driven only.
 * @param decorMethodInfo - Quoter DecoratingMethod and/or IDecorDetailInfo rows
 */
export const checkIfMTO = (
  decorMethodInfo: (DecoratingMethod | IDecorDetailInfo)[] = []
): boolean => {
  if (!Array.isArray(decorMethodInfo) || !decorMethodInfo.length) {
    return false;
  }

  return decorMethodInfo.some((method) => {
    if ('type' in method) {
      return (
        method.type === LACE_DECORATING_METHOD_ID && LACE_MTO_COLOR.includes(method.color)
      );
    }
    if ('decoratingMethod' in method) {
      return (
        method.decoratingMethod.id === LACE_DECORATING_METHOD_ID &&
        LACE_MTO_COLOR.includes(method.color || '')
      );
    }
    return false;
  });
};

export const getDesignDetailPricingReqPayload = (data: IDesignItem, quantity = 12) => {
  let locations: Location[] = [];
  let greekMarks = true;
  let collegiateMarks = true;

  const design = data.design as IDesignObj;

  const checkforDTPrintTypes =
    data.design.fromDesignTool && design.printInfo && !design.printTypeInfo;
  const printTypeInfo =
    typeof design.printTypeInfo === 'string'
      ? JSON.parse(design.printTypeInfo)
      : design.printTypeInfo;

  if (checkforDTPrintTypes) {
    locations = getDTLocations(design?.printInfo ?? '');
  } else {
    locations = mapPrintTypeInfoToQuoterLocations(printTypeInfo);
  }

  /** Decorating Method info */
  const decoratingMethods = mapDecorMethodInfoToQuoterDecoratingMethods(
    design.decorMethodInfo,
    data?.apparel?.category ?? null
  );

  const isMoreThan3Colors = printTypeInfo?.some(
    (location: { numberOfColors: number; printType: { id: number } }) => {
      if (location?.numberOfColors) {
        return (
          location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
          location?.printType?.id === 1
        );
      }
      return false;
    }
  );

  if (data.design?.designLicensing) {
    const designLicensing = data.design.designLicensing as IDesignLicensing;

    greekMarks = designLicensing.greek_status !== designLicensePriorityToStatusMap[8];
    collegiateMarks = designLicensing.collegiate_status !== designLicensePriorityToStatusMap[8];
  }

  const includesMto = checkIfMTO(decoratingMethods);
  const additionalShippingType = getAdditionalShippingType(
    data?.apparel?.isFlashEligible || false,
    isMoreThan3Colors,
    includesMto
  );

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const triggerAlphaError = urlParams?.get('triggerAlphaError');
  const variantSnapshot =
    data.variantSnapshot ??
    getVariantSnapshotFromTransformedDesigns(data.designGroupDesigns?.transformedDesigns);

  const payLoad: QuoterPayload = {
    apparels: [
      {
        external: data?.apparel?.isExternal || false,
        weight: data?.apparel?.weight ?? 0,
        locations,
        decoratingMethods,
        additionalShipping: {
          type: additionalShippingType,
        },
        quantity: [quantity],
        minimumDiscountQuantity: design?.designPricing?.discount_quantity ?? 0,
        isMtoProduct: includesMto ?? false,
        type: data?.apparel?.type ?? null,
        styleCode: data?.apparel?.styleCode ?? null,
        price: data?.apparel?.type === 1 ? null : data?.apparel?.price ?? null,
        colorName: data?.apparel?.color ?? null,
        category: data?.apparel?.category ?? null,
        hexCode: reverseHexCode(data?.apparel?.hexCode ?? ''),
        colorCode: data?.apparel?.colorCode ?? null,
        isGreek: !!(data?.organization_id && data?.organization_id !== 1) && greekMarks,
        isCollegiate:
          !!(data?.collegiate_school_id && data?.collegiate_school_id !== 1) && collegiateMarks,
        organization_id: data?.organization_id ?? null,
        school_id: data?.collegiate_school_id ?? null,
        markup: 0,
        zipCode: '',
        breakpoints: true,
        discount: design?.designPricing?.discount ?? 0,
        ...(data?.apparel?.type === 1 && { sizes: data?.apparel?.sizes ?? '' }),
        ...getPromoProductQuoterFields(variantSnapshot),
        triggerAlphaError: Boolean(triggerAlphaError)
      },
    ],
  };

  return payLoad;
};

/**
 * Calculate design group quantity for a given cart item
 * @param data - The cart item detail
 * @param allCartItems - All cart items to check for design group
 * @returns The total quantity for the design group or null if not applicable
 */
export const getDesignGroupQuantity = (
  data: ICartItemDetail,
  allCartItems?: ICartItemDetail[]
): number | null => {
  if (!allCartItems || allCartItems.length === 0) {
    return null;
  }

  const groupKey = data.designGroupItemId ?? data.proofItemId;
  const groupItems = allCartItems.filter(
    (item) => (item.designGroupItemId ?? item.proofItemId) === groupKey
  );

  if (groupItems.length > 1) {
    // Only calculate design group quantity if there are multiple items in the group
    return groupItems.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  return null;
};

export interface ICartPricingPayloadOptions {
  storeShippingLane?: StoreCartMoqShippingLane;
}

export const getCartPricingReqPayload = (
  data: ICartItemDetail,
  designGroupQuantity?: number | null,
  options: ICartPricingPayloadOptions = {}
) => {
  let locations: Location[] = [];

  const checkforDTPrintTypes = data.fromDesignTool && data.printTypes && !data.printTypeInfo;
  const printTypeInfo =
    typeof data.printTypeInfo === 'string' ? JSON.parse(data.printTypeInfo) : data.printTypeInfo;

  if (checkforDTPrintTypes) {
    locations = getDTLocations(data?.printTypes ?? '');
  } else {
    locations = mapPrintTypeInfoToQuoterLocations(printTypeInfo);
  }

  /** Decorating Method info */
  const decoratingMethods = mapDecorMethodInfoToQuoterDecoratingMethods(
    data.decorMethodInfo,
    data?.apparelCategory ?? null
  );

  const isMoreThan3Colors = printTypeInfo?.some(
    (location: { numberOfColors: number; printType: { id: number } }) => {
      if (location?.numberOfColors) {
        return (
          location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
          location?.printType?.id === 1
        );
      }
      return false;
    }
  );

  const includesMto = checkIfMTO(decoratingMethods);

  const additionalShippingType =
    options.storeShippingLane === 'individually_shipped'
      ? 'individually_shipped'
      : getAdditionalShippingType(
        data?.isFlashEligible || false,
        isMoreThan3Colors,
        data.isMadeToOrder || includesMto
      );

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const triggerAlphaError = urlParams?.get('triggerAlphaError');

  const payLoad: QuoterPayload = {
    apparels: [
      {
        external: data?.isExternalApparel ?? false,
        weight: data.apparelWeight ?? 0,
        locations,
        decoratingMethods,
        additionalShipping: {
          type: additionalShippingType,
        },
        quantity: [data.quantity],
        isMtoProduct: includesMto ?? false,
        type: data?.apparelType ?? null,
        styleCode: data?.apparelStyleCode ?? null,
        colorName: data?.apparelColor ?? null,
        price: data?.apparelType === 1 ? null : data?.apparelPrice ?? null,
        category: data?.apparelCategory ?? null,
        hexCode: reverseHexCode(data?.apparelHex),
        colorCode: data.apparelColorCode ?? null,
        isGreek:
          !!(data?.organizationId && data?.organizationId !== 1) &&
          data.designLicensing.greek_status !== designLicensePriorityToStatusMap[8],
        isCollegiate:
          !!(data?.collegiateId && data?.collegiateId !== 1) &&
          data.designLicensing.collegiate_status !== designLicensePriorityToStatusMap[8],
        organization_id: data.organizationId ?? null,
        school_id: data.collegiateId ?? null,
        markup: 0,
        zipCode: '',
        breakpoints: true,
        discount: data?.discount ?? 0,
        minimumDiscountQuantity: data?.designPricing?.discount_quantity ?? 0,
        ...(data?.apparelType === 1 && { sizes: data?.apparelSizes ?? '' }),
        ...getPromoProductQuoterFields(data.variantSnapshot),
        triggerAlphaError: Boolean(triggerAlphaError),
        ...(designGroupQuantity && !includesMto && { designGroupQuantity }),
      },
    ],
  };

  return payLoad;
};

export const checkDTPricingCompatible = (
  printInfo: string,
  streamlinedPrintInfo: IPrintTypeInfo[]
) => {
  let isPricingCompatible = true;

  /** Checking older print info only when the design doesn't have streamlined print information */
  if (!streamlinedPrintInfo || !streamlinedPrintInfo?.length) {
    if (printInfo?.trim()) {
      const hasInCompatiblePrintType = hasPrintTypeOtherThanScreenPrint(printInfo);

      if (hasInCompatiblePrintType) {
        isPricingCompatible = false;
      }
    }
  }

  return isPricingCompatible;
};

/**
 * Method to check if design is blocked for checkout.
 */
export const getBlockCheckoutCondition = (
  fromDesignTool: boolean,
  blockSelfCheckout: boolean,
  designPricing: IDesignPricing
): boolean => {
  if (fromDesignTool) {
    if (designPricing) {
      return blockSelfCheckout;
    }
    return false;
  }
  if (designPricing) {
    return blockSelfCheckout;
  }
  return true;
};

/**
 * Method to check if design is blocked for checkout.
 *
 * @param designItem - design item
 */
export const isDesignBlockedForCheckout = (designItem: IDesignItem | IDesignProofItem) => {
  const designPricing = designItem?.design?.designPricing as IDesignPricing;
  const blockSelfCheckout = designPricing?.block_self_checkout;
  let isFromDesignTool = false;

  if (designItem.design) {
    isFromDesignTool = (
      'from_design_tool' in designItem.design
        ? designItem.design.from_design_tool
        : designItem.design.fromDesignTool
    ) as boolean;
  }

  return getBlockCheckoutCondition(isFromDesignTool, blockSelfCheckout, designPricing);
};

/**
 * Method to get the ship prices for given key from api response object
 *
 * @param shippingOptions - Ship pricing array
 * @param types - ship types
 */
export const getShippingPrices = (
  shippingOptions: IAdditionalShippingPriceRes[],
  types: string[]
): { [key: string]: number } => {
  const prices: { [key: string]: number } = {};

  shippingOptions.forEach((option) => {
    if (types.includes(option.type)) {
      prices[option.type] = option.additionalShippingPrice;
    }
  });

  return prices;
};

/**
 * Method to return the order pricing info message.
 * @param minQuantity - The minimum quantity required.
 * @param currentQuantity - The current quantity entered.
 * @param requiredQuantity - The quantity required to reach the next breakpoint.
 * @param nextBreakPointPrice - The price at the next breakpoint.
 */
export const getOrderQuantityPricingMessage = (
  minQuantity: number,
  currentQuantity: number,
  requiredQuantity: number,
  nextBreakPointPrice: number,
  currentShipType?: string
) => {
  let message = '';

  if (currentQuantity === minQuantity) {
    message = `Our minimum order quantity for this product is ${minQuantity}. Add ${requiredQuantity} more to bring the price down to ${currencyFormat(
      nextBreakPointPrice
    )} per item.`;
  } else if (currentQuantity > minQuantity) {
    message = `If your order ${requiredQuantity} more, the price will drop to ${currencyFormat(
      nextBreakPointPrice
    )} per item.`;
  } else if (currentQuantity < minQuantity) {
    if (currentShipType) {
      message = `The minimum order qty. for this product is ${minQuantity} for ${currentShipType} shipping.`;
    } else {
      message = `The minimum order qty. for this product is ${minQuantity}.`;
    }
  }

  return message;
};

export const DEFAULT_MOQ = 12;
export const MTO_MOQ = 50;

/**
 * For MTO (e.g. Lace), enforce the higher of product MTO rules (`MTO_MOQ`) and the quoter
 * shipping-lane MOQ from `resolveQuoteMoqForHq` / `additionalShippingPriceObject`.
 */
export const resolveEffectiveMtoMoq = (shippingLaneMoq: number | undefined): number =>
  Math.max(MTO_MOQ, shippingLaneMoq ?? DEFAULT_MOQ);

/** Same screen-print >3 colors rule as quoter payloads (single design). */
export const getIsMoreThan3ColorsFromDesignItem = (designItem: IDesignItem): boolean => {
  const design = designItem.design as IDesignObj;
  const printTypeInfo =
    typeof design?.printTypeInfo === 'string'
      ? JSON.parse(design.printTypeInfo)
      : design?.printTypeInfo;
  return (
    printTypeInfo?.some(
      (location: IPrintTypeInfo) =>
        location?.numberOfColors &&
        location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
        location?.printType?.id === 1
    ) ?? false
  );
};

/** Same rule for design-group product rows. */
export const getIsMoreThan3ColorsFromDesignGroupProduct = (
  productOption: IDesignGroupProductOption
): boolean => {
  const printTypeInfo =
    typeof productOption.printTypeInfo === 'string'
      ? JSON.parse(productOption.printTypeInfo)
      : productOption.printTypeInfo;
  return (
    printTypeInfo?.some(
      (location: IPrintTypeInfo) =>
        location?.numberOfColors &&
        location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
        location?.printType?.id === 1
    ) ?? false
  );
};

/**
 * HQ Recent Designs: root `quoteBreakdown.moq` follows the *requested* additionalShipping type
 * (often `flash` when flash-eligible). When that lane is `eligible: false` but another lane
 * (e.g. `mto`) is eligible, validation must use that lane's `moq` from `additionalShippingPriceObject`.
 */
export const resolveQuoteMoqForHq = (
  quoteBreakdown: IQuoteResponse,
  ctx: {
    isFlashEligible: boolean;
    isMoreThan3Colors: boolean;
    includesMto: boolean;
    /** When true (e.g. lace MTO colors), use the MTO lane MOQ even if `eligible === false` on that row */
    forceMtoLaneEligible?: boolean;
  }
): number => {
  const rows = quoteBreakdown.additionalShippingPriceObject;
  const rootMoq = quoteBreakdown.moq;
  const productFloorMoq = rootMoq ?? quoteBreakdown.productMOQ ?? DEFAULT_MOQ;

  if (!rows?.length) {
    return productFloorMoq;
  }

  const findRow = (type: string) =>
    rows.find((r) => (r.type ?? '').toLowerCase() === type.toLowerCase());

  const tryOrder: readonly string[] = ctx.forceMtoLaneEligible
    ? (['mto', 'individually_shipped'] as const)
    : ([...SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY, 'individually_shipped'] as const);

  const matchedRow = tryOrder
    .map((type) => ({ type, row: findRow(type) }))
    .find(({ type, row }) => {
      if (!row || row.moq == null) return false;
      if (ctx.forceMtoLaneEligible && type === 'mto') return true;
      return row.eligible !== false;
    })?.row;

  const laneResolvedMoq = rootMoq ?? matchedRow?.moq ?? DEFAULT_MOQ;
  return Math.max(laneResolvedMoq, productFloorMoq);
};

/**
 * Same lane priority as {@link resolveQuoteMoqForHq}, but only considers shipping types that are
 * eligible for the **entire** design group (caller supplies a predicate, e.g. from quoter rows
 * rolled up across all product-color lines).
 */
export const resolveQuoteMoqForHqWithGroupEligibility = (
  quoteBreakdown: IQuoteResponse,
  isShipTypeEligibleForGroup: (shipTypeKey: string) => boolean,
  forceMtoLaneEligible: boolean,
  maxTimeline?: ITimelineElement[]
): number => {
  const rows = quoteBreakdown.additionalShippingPriceObject;
  const rootMoq = quoteBreakdown.moq;
  const productFloorMoq = rootMoq ?? quoteBreakdown.productMOQ ?? DEFAULT_MOQ;

  if (!rows?.length) {
    return productFloorMoq;
  }

  const findRow = (type: string) =>
    rows.find((r) => (r.type ?? '').toLowerCase() === type.toLowerCase());

  const tryOrder: readonly string[] = [...SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY, 'individually_shipped'];

  const eligibleLanes = tryOrder.filter((type) => isShipTypeEligibleForGroup(type));
  const lanesForMoqPick = forceMtoLaneEligible
    ? eligibleLanes.filter((type) => type === 'mto')
    : eligibleLanes;

  if (lanesForMoqPick.length > 0) {
    const matchedRow = lanesForMoqPick
      .map((type) => ({ type, row: findRow(type) }))
      .find(({ type, row }) => {
        if (!row || row.moq == null) return false;
        if (forceMtoLaneEligible && type === 'mto') return true;
        return row.eligible !== false;
      })?.row;

    if (matchedRow?.moq != null) {
      return Math.max(matchedRow.moq, productFloorMoq);
    }
  }

  if (maxTimeline?.length) {
    const timelineEligibleLane = maxTimeline.find((t) => t.eligible !== false);
    if (timelineEligibleLane) {
      const quoterRow = findRow(timelineEligibleLane.type);
      if (quoterRow?.moq != null) return Math.max(quoterRow.moq, productFloorMoq);
    }
  }

  return productFloorMoq;
};

/**
 * Validates if quantity meets MOQ requirements for saving design pricing.
 * Prevents API calls when validation fails.
 * 
 * @param quantity - The new quantity value
 * @param isMtoItem - Whether the item is MTO (Make to Order)
 * @param currentNonMtoTotal - Current total quantity of all non-MTO items (before this change)
 * @param oldQuantity - Previous quantity for this item (0 if new)
 * @param maxMoq - Maximum MOQ requirement for non-MTO items
 * @returns true if quantity meets MOQ requirements and API call should proceed
 */
export const shouldSaveDesignPricing = (
  quantity: number,
  isMtoItem: boolean,
  currentNonMtoTotal: number,
  oldQuantity: number,
  maxMoq: number
): boolean => {
  // Invalid quantities should not trigger API call
  if (quantity <= 0 || !Number.isFinite(quantity)) {
    return false;
  }

  if (isMtoItem) {
    // MTO items: only save if quantity >= MTO_MOQ
    return quantity >= MTO_MOQ;
  }

  // Non-MTO items: check if new total meets max MOQ requirement
  const newNonMtoTotal = currentNonMtoTotal - oldQuantity + quantity;
  return newNonMtoTotal >= maxMoq;
};

/**
 * Calculates the pricing payload for a design item based on the provided proof item details and getQuote function.
 *
 * @param {IDesignItem} proofItemDetails - The details of the design item.
 * @param {GetQuoteMutationFunction} getQuote - The function to get the quote.
 * @return {Promise<DesignPricingPayload | null>} The pricing payload for the design item, or null if it cannot be calculated.
 */
export const calculatePricingPayload = async (
  proofItemDetails: IDesignItem,
  getQuote: GetQuoteMutationFunction
): Promise<DesignPricingPayload | null> => {
  if (proofItemDetails.design.designPricing) {
    const designQuantity =
      (proofItemDetails.design.designPricing as { quantity: number }).quantity === 0
        ? 12
        : (proofItemDetails.design.designPricing as { quantity: number }).quantity;
    const quoterPayload = getDesignDetailPricingReqPayload(proofItemDetails, designQuantity);
    const quoterResponse = await getQuote({ data: quoterPayload });
    const isCheckoutBlocked = (
      proofItemDetails.design.designPricing as { block_self_checkout: boolean }
    ).block_self_checkout;

    let pricingPayload: DesignPricingPayload | null = null;

    if (isCheckoutBlocked) {
      pricingPayload = {
        ship_type: (proofItemDetails.design.designPricing as { ship_type: string }).ship_type,
        block_self_checkout: true,
        quantity: designQuantity,
      };
    } else if (quoterResponse && 'data' in quoterResponse) {
      const quoteBreakdown: IQuoteResponse = quoterResponse.data[0];

      pricingPayload = {
        ship_type: (proofItemDetails.design.designPricing as { ship_type: string }).ship_type,
        quantity: +designQuantity,
        unit_price: +(proofItemDetails.design.designPricing as { discount: number }).discount
          ? quoteBreakdown.finalPricePerUnitAfterDiscount
          : quoteBreakdown.pricePerUnitWithCollegiate,
        discount: +(proofItemDetails.design.designPricing as { discount: number }).discount,
        discount_quantity: +(proofItemDetails.design.designPricing as { discount_quantity: number })
          .discount_quantity,
        ship_total: +quoteBreakdown.shipping,
        gpm: +quoteBreakdown.GPM,
        block_self_checkout: false,
      };
    }

    return pricingPayload;
  }

  const quoterPayload = getDesignDetailPricingReqPayload(proofItemDetails, 12);
  const quoterResponse = await getQuote({ data: quoterPayload });
  let pricingPayload: DesignPricingPayload | null = null;

  if (quoterResponse && 'data' in quoterResponse) {
    const quoteBreakdown: IQuoteResponse = quoterResponse.data[0];

    pricingPayload = {
      ship_type: 'standard',
      quantity: 12,
      unit_price: quoteBreakdown.pricePerUnitWithCollegiate,
      discount: quoteBreakdown.discount,
      ship_total: +quoteBreakdown.shipping,
      gpm: +quoteBreakdown.GPM,
      block_self_checkout: false,
    };
  }

  return pricingPayload;
};

/**
 * Creates a quoter payload from a single color option of the IDesignGroupProductOption interface.
 * This function is used when pricing a specific color variant from a design group.
 * Now utilizes the updated interface properties including weight, type, category, and colorCode.
 *
 * @param colorOption - The color option from IDesignGroupProductOption.colors array
 * @param productOption - The parent product option containing print types, decorating methods, and apparel details
 * @param quantity - The quantity for pricing calculation (default: 12)
 * @param organizationId - The organization ID for the user
 * @param schoolId - The Schoo lID for the user
 * @returns QuoterPayload - The payload for the quoter API
 */
export const getDesignGroupColorOptionPricingReqPayload = (
  colorOption: IDesignGroupColorOption,
  productOption: IDesignGroupProductOption,
  quantity: number,
  organizationId?: number | null,
  schoolId?: number | null,
  totalQuantity?: number | null
) => {
  let locations: Location[] = [];
  let greekMarks = true;
  let collegiateMarks = true;

  const checkforDTPrintTypes =
    productOption?.fromDesignTool && productOption.printInfo && !productOption.printTypeInfo;
  const printTypeInfo =
    typeof productOption.printTypeInfo === 'string'
      ? JSON.parse(productOption.printTypeInfo)
      : productOption.printTypeInfo;

  if (checkforDTPrintTypes) {
    locations = getDTLocations(productOption?.printInfo ?? '');
  } else {
    locations = mapPrintTypeInfoToQuoterLocations(printTypeInfo);
  }

  const { apparelData } = productOption;
  const decoratingMethods = mapDecorMethodInfoToQuoterDecoratingMethods(
    productOption.decorMethodInfo,
    apparelData?.category ?? null
  );

  const isMoreThan3Colors = printTypeInfo?.some((location: IPrintTypeInfo) => {
    if (location?.numberOfColors) {
      return (
        location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
        location?.printType?.id === 1
      );
    }
    return false;
  });

  if (colorOption.designLicensing) {
    const { designLicensing } = colorOption;

    greekMarks = designLicensing.greek_status !== designLicensePriorityToStatusMap[8];
    collegiateMarks = designLicensing.collegiate_status !== designLicensePriorityToStatusMap[8];
  }

  const includesMto = checkIfMTO(decoratingMethods);
  const additionalShippingType = getAdditionalShippingType(
    productOption.isFlashEligible || false,
    isMoreThan3Colors,
    includesMto
  );

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const triggerAlphaError = urlParams?.get('triggerAlphaError');
  const quantityToSend = quantity || (includesMto ? MTO_MOQ : DEFAULT_MOQ);
  const payLoad: QuoterPayload = {
    apparels: [
      {
        external: productOption.isExternalApparel || false,
        weight: apparelData?.weight ?? 0,
        locations,
        decoratingMethods,
        additionalShipping: {
          type: additionalShippingType,
        },
        quantity: [quantityToSend],
        minimumDiscountQuantity: colorOption?.designPricing?.discount_quantity ?? 0,
        isMtoProduct: includesMto ?? false,
        type: colorOption?.apparel?.type ?? 4,
        styleCode: productOption.styleCode ?? null,
        price: colorOption?.apparel?.type === 1 ? null : colorOption?.apparel?.price ?? 0,
        colorName: colorOption.colorName ?? null,
        category: apparelData?.category ?? null,
        hexCode: reverseHexCode(colorOption.colorHex ?? ''),
        colorCode: colorOption.colorCode ?? null,
        isGreek: !!(organizationId && organizationId !== 1) && greekMarks,
        isCollegiate: !!(schoolId && schoolId !== 1) && collegiateMarks,
        organization_id: organizationId ?? null,
        school_id: schoolId ?? null,
        markup: 0,
        zipCode: '',
        breakpoints: true,
        discount: colorOption?.designPricing?.discount ?? 0,
        ...( apparelData?.type === 1 && { sizes:  apparelData.sizes ?? '' }),
        ...getPromoProductQuoterFields(productOption.variantSnapshot),
        triggerAlphaError: Boolean(triggerAlphaError),
        designGroupQuantity: !includesMto ? totalQuantity : null,
      },
    ],
  };
  return payLoad;
};

export const getDesignGroupItemKey = (
  styleCode: string,
  colorName: string,
  designId: number
): string => `${styleCode}-${colorName}-${designId}`;

/**
 * Single source of truth for the unit price shown for a quoted cart line.
 *
 * The quoter returns several unit-price fields that only coincide when there is
 * no discount: `pricePerUnit` is pre-discount, while
 * `finalPricePerUnitAfterDiscount` is what the customer is actually charged and
 * what the V4 quoter displays. Every cart surface must resolve through here so
 * the drawer, checkout and V4 cannot drift apart.
 *
 * Returns `null` when the quote has no usable price, which is distinct from a
 * genuine $0 price (`|| 0` would collapse the two).
 */
export const resolveQuotedCartUnitPrice = (
  item: Pick<ICartItemDetail, 'discount'>,
  quoteBreakdown: IQuoteResponse
): number | null => {
  if (item.discount) {
    const discountedPrice = Number(quoteBreakdown.finalPricePerUnitAfterDiscount);
    return Number.isFinite(discountedPrice) ? discountedPrice : null;
  }

  const collegiatePrice = Number(quoteBreakdown.pricePerUnitWithCollegiate);
  if (Number.isFinite(collegiatePrice)) {
    return collegiatePrice;
  }

  const price = Number(quoteBreakdown.pricePerUnit);
  return Number.isFinite(price) ? price : null;
};

export const getDesignGroupQuoteUnitPrice = (quoteResponse: IQuoteResponse): number =>
  quoteResponse.finalPricePerUnitAfterDiscount ?? quoteResponse.pricePerUnit ?? 0;

const buildDesignGroupQuantitiesMap = (design: IDesignItem): Record<string, number> => {
  const transformedDesigns = design.designGroupDesigns?.transformedDesigns ?? [];
  const quantities: Record<string, number> = {};

  transformedDesigns.forEach((product) => {
    product.colors.forEach((color) => {
      const key = getDesignGroupItemKey(product.styleCode, color.colorName, color.designId);
      quantities[key] = color.designPricing?.quantity || 0;
    });
  });

  return quantities;
};

export const getDesignGroupUnitPricesFromQuotes = (
  design: IDesignItem,
  quotes: Record<string, IQuoteResponse>
): Record<string, number> => {
  const transformedDesigns = design.designGroupDesigns?.transformedDesigns ?? [];
  const unitPrices: Record<string, number> = {};

  transformedDesigns.forEach((product) => {
    product.colors.forEach((color) => {
      const key = getDesignGroupItemKey(product.styleCode, color.colorName, color.designId);
      const quoteResponse = quotes[key];

      if (quoteResponse) {
        unitPrices[key] = getDesignGroupQuoteUnitPrice(quoteResponse);
      }
    });
  });

  return unitPrices;
};

export const fetchDesignGroupQuotes = async (
  design: IDesignItem
): Promise<Record<string, IQuoteResponse>> => {
  const transformedDesigns = design.designGroupDesigns?.transformedDesigns ?? [];
  const quantities = buildDesignGroupQuantitiesMap(design);
  const totalQuantity = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  if (!totalQuantity) return {};

  const quoteResults = await Promise.all(
    transformedDesigns.flatMap((product) =>
      product.colors.map(async (color) => {
        const key = getDesignGroupItemKey(product.styleCode, color.colorName, color.designId);
        const quotePayload = getDesignGroupColorOptionPricingReqPayload(
          color,
          product,
          quantities[key],
          design.organization_id ?? 0,
          design.collegiate_school_id ?? 0,
          totalQuantity
        );

        try {
          const response = await fetch(
            `${V3_APP_BASE_URL}api/v4/new-quoter?stockLevelsOnly=false&isHQ=true`,
            {
              method: 'POST',
              body: JSON.stringify(quotePayload),
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            }
          );

          if (!response.ok) {
            console.error(`Quote API returned ${response.status} for ${key}`);
            return null;
          }

          const responseData = await response.json();
          const itemData = responseData?.data[0] as IQuoteResponse | undefined;

          if (!itemData) {
            return null;
          }

          return [key, itemData] as const;
        } catch (error) {
          console.error(`Failed to fetch quote for ${key}:`, error);
          return null;
        }
      })
    )
  );

  return Object.fromEntries(
    quoteResults.filter((result): result is readonly [string, IQuoteResponse] => result !== null)
  );
};

export function useFetchQuouteForDesignGroups() {
  const [isQuoterLoading, setIsQuoterLoading] = useState(false);
  const [pricePerItem, setPricePerItem] = useState(0);
  const [moq, setMoq] = useState(12);

  const fetchQuoteForDesignGroups = async (payload: QuoterPayload) => {
    try {
      setIsQuoterLoading(true);
      const response: Response = await fetch(
        `${V3_APP_BASE_URL}api/v4/new-quoter?stockLevelsOnly=false&isHQ=true`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );
      const responseData = await response.json();
      const itemData = responseData?.data[0];
      if (itemData?.finalPricePerUnitAfterDiscount) {
        setPricePerItem(itemData?.finalPricePerUnitAfterDiscount);
      }
      if (itemData?.moq) {
        setMoq(itemData?.moq);
      }
      setIsQuoterLoading(false);
    } catch (error) {
      setIsQuoterLoading(false);
      showToastrMessage('Failed to fetch quote', 'failure');
    }
  };

  return {
    isQuoterLoading,
    fetchQuoteForDesignGroups,
    pricePerItem,
    moq,
  };
}
